<template>
  <v-card class="ma-3 pa-3">
    <v-card-title>
      <h2>
        {{ $t('SEGUIMIENTO DE CLIENTES') }}
      </h2>
    </v-card-title>

    <!-- Action buttons -->
    <v-layout align-center justify-start row fill-height>
      <div class="mt-2">
        <v-btn color="primary" @click="addNewTracking()">
          <v-icon left>mdi-plus</v-icon>
          {{ $t('Añadir Seguimiento') }}
        </v-btn>
        <v-btn
          v-if="selectedItems.length > 0"
          color="light"
          @click="exportToExcel()"
          :disabled="exportingExcel"
        >
          <v-icon left>mdi-file-excel-outline</v-icon>
          {{ $t('Exportar a excel') }}
          <v-progress-circular
            v-if="exportingExcel"
            class="ml-2"
            :size="20"
            indeterminate
            color="black"
          ></v-progress-circular>
        </v-btn>
      </div>
    </v-layout>
    <!-- End action buttons -->

    <!-- Filters -->
    <v-layout align-center justify-end row fill-height class="mt-3 mb-4">
      <div class="ml-3">
        <v-autocomplete
          color="primary"
          v-model="filters.company_ids"
          multiple
          chips
          deletable-chips
          hide-details
          :items="companyList"
          :label="$t('Empresa')"
          row
        ></v-autocomplete>
      </div>
      <div class="ml-3">
        <v-autocomplete
          color="primary"
          v-model="filters.consultant_ids"
          multiple
          chips
          deletable-chips
          hide-details
          :items="consultantList"
          :label="$t('Asesor/a Fiscal')"
          row
        ></v-autocomplete>
      </div>
      <div class="ml-3">
        <v-menu
          v-model="showDateFromPicker"
          :close-on-content-click="false"
          :nudge-right="40"
          lazy
          transition="scale-transition"
          offset-y
          full-width
          min-width="290px"
        >
          <template v-slot:activator="{ on }">
            <v-text-field
              ref="date"
              v-model="formattedDateFrom"
              class="pr-2"
              label="Desde"
              hide-details
              readonly
              clearable
              v-on="on"
            ></v-text-field>
          </template>
          <v-date-picker
            v-model="filters.date_from"
            @input="showDateFromPicker = false"
            locale="es"
            first-day-of-week="1"
            :reactive="true"
          ></v-date-picker>
        </v-menu>
      </div>
      <div class="ml-3">
        <v-menu
          v-model="showDateToPicker"
          :close-on-content-click="false"
          :nudge-right="40"
          lazy
          transition="scale-transition"
          offset-y
          full-width
          min-width="290px"
        >
          <template v-slot:activator="{ on }">
            <v-text-field
              ref="date"
              v-model="formattedDateTo"
              class="pr-2"
              label="Hasta"
              readonly
              hide-details
              clearable
              v-on="on"
            ></v-text-field>
          </template>
          <v-date-picker
            v-model="filters.date_to"
            @input="showDateToPicker = false"
            locale="es"
            first-day-of-week="1"
            :reactive="true"
          ></v-date-picker>
        </v-menu>
      </div>
      <v-flex xs12 md4>
        <div class="ml-3">
          <v-text-field
            v-model="filters.search"
            append-icon="search"
            label="Buscar"
            single-line
            hide-details
          ></v-text-field>
        </div>
      </v-flex>
    </v-layout>
    <!-- End filters -->

    <!-- Modal to add new tracking -->
    <v-layout row justify-center>
      <customer-tracking-form
        :show="showCreateTrackingForm"
        :key="trackingFormKey"
        :customer-tracking-id="selectedTrackingId"
        @close="onCloseTrackingForm"
        @on-saved-tracking="onSavedTracking"
        @on-show-freelance-guide="showFreelanceGuide = true"
        @on-show-society-guide="showSocietyGuide = true"
        @on-show-laboral-guide="showLaboralGuide = true"
      ></customer-tracking-form>
    </v-layout>
    <!-- End modal -->

    <v-layout row justify-center>
      <customer-tracking-freelance-guide
        :show="showFreelanceGuide"
        @close="showFreelanceGuide = false"
      ></customer-tracking-freelance-guide>
    </v-layout>

    <v-layout row justify-center>
      <customer-tracking-society-guide
        :show="showSocietyGuide"
        @close="showSocietyGuide = false"
      ></customer-tracking-society-guide>
    </v-layout>

    <v-layout row justify-center>
      <customer-tracking-laboral-guide
        :show="showLaboralGuide"
        @close="showLaboralGuide = false"
      ></customer-tracking-laboral-guide>
    </v-layout>

    <!-- Modal to delete tracking -->
    <v-dialog v-model="showDeleteTrackingModal" persistent max-width="600px">
      <v-card>
        <v-card-title>
          <span class="headline">{{ $t('Eliminar') }}</span>
        </v-card-title>
        <v-card-text>
          <v-container grid-list-md>
            <v-layout wrap>
              <v-flex xs12 class="text-xs-center">
                <h2>
                  ¿Estás seguro de eliminar el seguimiento seleccionado?
                </h2>
                <small>Esta acción no es reversible</small>
              </v-flex>
            </v-layout>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-btn color="blue darken-1" flat @click="closeDeleteTrackingModal">
            {{ $t('Cerrar') }}
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn color="error" @click="confirmDeleteTracking">
            {{ $t('Eliminar') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Data table with customer tracking -->
    <v-data-table
      ref="customerTrackingTable"
      v-model="selectedItems"
      :loading="loading"
      :headers="headers"
      :items="customerTrackingItems"
      :pagination.sync="pagination"
      :rows-per-page-items="[10, 20, 30, 50, 100]"
      :total-items="pagination.totalItems"
      select-all
      class="tracking-table"
    >
      <template v-slot:no-data>
        <v-alert :value="true" color="info" icon="info" outline>
          {{ $t('No hay información que mostrar con los filtros seleccionados :(') }}
        </v-alert>
      </template>
      <template v-slot:headers="props">
        <tr>
          <th v-if="props.headers.length > 0">
            <v-checkbox
              v-model="selectAllRows"
              @change="selectAllTableRows()"
              primary
              hide-details
            ></v-checkbox>
          </th>
          <th
            v-for="header in props.headers"
            :key="`th_${header.value}`"
            :class="[
              'text-xs-center',
              header.sortable ? 'column sortable' : '',
              pagination.descending ? 'desc' : 'asc',
              header.value === pagination.sortBy ? 'active' : '',
            ]"
            @click="header.sortable ? changeSort(header.value) : () => {}"
          >
            <v-icon v-if="header.sortable" small>arrow_upward</v-icon>
            {{ header.text }}
          </th>
          <th v-if="props.headers.length > 0" class="text-xs-center">ACCIONES</th>
        </tr>
      </template>
      <template v-slot:items="props">
        <tr :class="{ 'row-pending': !!props.item.pending_actions }">
          <td>
            <v-checkbox
              v-model="props.selected"
              @change="onSelectItem"
              primary
              hide-details
            ></v-checkbox>
          </td>
          <td class="text-xs-center">
            {{ props.item.company_name }}
          </td>
          <td class="text-xs-center">
            {{ props.item.contact_name }}
          </td>
          <td class="text-xs-center text-capitalize">
            {{ props.item.type }}
          </td>
          <td class="text-xs-center">
            {{ formatDate(props.item.date) }}
          </td>
          <td class="text-xs-center">
            {{ props.item.title | truncate(60, '...') }}
          </td>
          <td class="text-xs-center">
            <v-icon small class="mr-2" @click="editTracking(props.item)">
              edit
            </v-icon>
            <v-icon small @click="deleteTracking(props.item)">
              delete
            </v-icon>
          </td>
        </tr>
      </template>
    </v-data-table>
    <v-snackbar v-model="snack" :timeout="3000" :color="snackColor">
      {{ snackText }}
    </v-snackbar>
  </v-card>
</template>

<script>
import CustomerTrackingForm from '@/modules/admin/customer-tracking/components/CustomerTrackingForm.vue';
import apiCtrl from '@/services/api/abstract/abstract-api';
import moment from 'moment';
import EmpresaAPI from '@/modules/admin/aplifisa/services/fiscal';
import ConsultantAPI from '@/modules/admin/customers-and-consultants/services/consultantAPI';
import CustomerTrackingFreelanceGuide from './CustomerTrackingFreelanceGuide.vue';
import CustomerTrackingLaboralGuide from './CustomerTrackingLaboralGuide.vue';
import CustomerTrackingSocietyGuide from './CustomerTrackingSocietyGuide.vue';

export default {
  name: 'CustomerTrackingTable',
  components: {
    CustomerTrackingFreelanceGuide,
    CustomerTrackingForm,
    CustomerTrackingSocietyGuide,
    CustomerTrackingLaboralGuide,
  },
  props: ['consultantId'],
  data() {
    return {
      showCreateTrackingForm: false,
      showDeleteTrackingModal: false,
      showFreelanceGuide: false,
      showSocietyGuide: false,
      showLaboralGuide: false,
      creatingTracking: false,
      selectedItems: [],
      selectAllRows: false,
      loading: false,
      headers: [],
      customerTrackingItems: [],
      snack: false,
      snackText: '',
      snackColor: 'info',
      trackingFormKey: 0,
      selectedTrackingId: null,
      consultantList: [],
      companyList: [],
      showDateFromPicker: false,
      showDateToPicker: false,
      pagination: {
        descending: true,
        page: 1,
        rowsPerPage: 10, // -1 for All",
        sortBy: '',
        totalItems: 0,
      },
      sortTable: false,
      filters: {
        search: '',
        consultant_ids: [],
        company_ids: [],
        columns: ['all'],
        date_from: null,
        date_to: null,
      },
    };
  },
  computed: {
    trackingKeyChanged() {
      return `${this.pagination.page}-${this.pagination.rowsPerPage}-${this.pagination.sortBy}-${
        this.pagination.descending
      }-${this.filters.search}-${this.filters.consultant_ids}-${this.filters.company_ids}-${
        this.filters.date_from
      }-${this.filters.date_to}`;
    },
    formattedDateFrom() {
      return this.filters.date_from ? moment(this.filters.date_from).format('DD/MM/YYYY') : '';
    },
    formattedDateTo() {
      return this.filters.date_to ? moment(this.filters.date_to).format('DD/MM/YYYY') : '';
    },
  },
  beforeMount() {
    this.fetchCustomerTrackingList();
    this.getConsultantsList();
    this.getCompaniesList();
  },
  methods: {
    formatDate(date) {
      return moment(date).format('DD/MM/YYYY');
    },
    addNewTracking() {
      this.showCreateTrackingForm = true;
    },
    onCloseTrackingForm() {
      this.showCreateTrackingForm = false;
      this.trackingFormKey++;
      this.selectedTrackingId = null;
    },
    onSavedTracking() {
      this.onCloseTrackingForm();
      this.fetchCustomerTrackingList();
    },
    fetchCustomerTrackingList() {
      this.loading = true;
      const config = {
        params: {
          page: this.pagination.page,
          per_page: this.pagination.rowsPerPage,
          sort_by: this.pagination.sortBy,
          descending: this.pagination.descending,
          consultant_ids: this.filters.consultant_ids,
          company_ids: this.filters.company_ids,
          search: this.filters.search,
          date_from: this.filters.date_from,
          date_to: this.filters.date_to,
        },
      };
      apiCtrl
        .get('/admin/customer-tracking/list', config)
        .then(res => {
          console.log(res.data);
          this.customerTrackingItems = res.data.data;
          this.headers = res.data.headers;
          this.pagination = {
            descending: this.pagination.descending,
            page: this.pagination.page,
            rowsPerPage: Number(res.data.meta.per_page), // -1 for All",
            sortBy: this.pagination.sortBy,
            totalItems: Number(res.data.meta.total),
          };
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
          // this.showSnackMessage(
          //   'error',
          //   'Se ha producido un error al cargar las obligaciones fiscales',
          // );
        });
      // this.loading = true;
      // const config = {
      //   params: {
      //     page: this.pagination.page,
      //     per_page: this.pagination.rowsPerPage,
      //     search: this.filters.search,
      //     sort_by: this.pagination.sortBy,
      //     descending: this.pagination.descending,
      //     consultant_ids: this.consultantId,
      //   },
      // };
      // this.$store.dispatch('customerTracking/fetchCustomerTrackingList', config).then(response => {
      //   this.customerTrackingItems = response.data.data;
      //   this.headers = response.data.dataTable.headers;
      //   this.pagination = {
      //     descending: this.pagination.descending,
      //     page: this.pagination.page,
      //     rowsPerPage: Number(response.data.meta.per_page), // -1 for All",
      //     sortBy: this.pagination.sortBy,
      //     totalItems: Number(response.data.meta.total),
      //   };
      //   this.loading = false;
      // });
    },
    selectAllTableRows() {
      if (
        this.selectedItems.length === 0 ||
        (this.selectedItems.length >= 1 &&
          this.selectedItems.length < this.customerTrackingItems.length)
      ) {
        let selected = [];
        this.customerTrackingItems.forEach(item => {
          selected.push(item);
        });
        this.selectedItems = selected;
      } else if (this.selectedItems.length === this.customerTrackingItems.length) {
        this.selectedItems = [];
      }
    },
    onSelectItem(value) {
      if (!value && this.selectAllRows) {
        this.selectAllRows = false;
      }
    },
    changeSort(column) {
      if (this.pagination.sortBy === column) {
        this.pagination.descending = !this.pagination.descending;
      } else {
        this.pagination.sortBy = column;
        this.pagination.descending = false;
      }
    },
    editTracking(tracking) {
      this.selectedTrackingId = tracking.id;
      this.trackingFormKey++;
      this.showCreateTrackingForm = true;
    },
    deleteTracking(tracking) {
      this.selectedTrackingId = tracking.id;
      this.showDeleteTrackingModal = true;
    },
    closeDeleteTrackingModal() {
      this.showDeleteTrackingModal = false;
      this.selectedTrackingId = null;
    },
    confirmDeleteTracking() {
      apiCtrl
        .delete(`/admin/customer-tracking/delete/${this.selectedTrackingId}`)
        .then(() => {
          this.closeDeleteTrackingModal();
          this.fetchCustomerTrackingList();
        })
        .catch(err => {
          console.log(err);
          this.closeDeleteTrackingModal();
        });
    },
    getCompaniesList() {
      EmpresaAPI.getFiscalDataInputSelect().then(response => {
        this.companyList = response.data;
      });
    },
    getConsultantsList() {
      ConsultantAPI.getConsultantInputSelect().then(response => {
        this.consultantList = response.data;
      });
    },
    async exportToExcel() {
      this.exportingExcel = true;
      import('../../../../plugins/Export2Excel.js').then(async excel => {
        let selectedIds = [];
        if (!this.selectAllRows) {
          this.selectedItems.forEach(item => {
            selectedIds = selectedIds.concat(item['id']);
          });
        }
        const config = {
          params: {
            page: 1,
            per_page: -1,
            sort_by: this.pagination.sortBy,
            descending: this.pagination.descending,
            consultant_ids: this.filters.consultant_ids,
            company_ids: this.filters.company_ids,
            search: this.filters.search,
            date_from: this.filters.date_from,
            date_to: this.filters.date_to,
            selected_ids: selectedIds,
          },
        };

        let headers = [];
        let dataToExport = [];
        await apiCtrl
          .get(`/admin/customer-tracking/export-excel`, config)
          .then(res => {
            headers = res.data.headers;
            dataToExport = res.data.data;
          })
          .catch(() => {
            this.showSnackMessage('error', 'Se ha producido un error al exportar los datos');
            this.exportingExcel = false;
          });
        let exportHeaders = headers.map(header => header.text);
        const exportColumns = headers.map(header => header.value);
        const exportList = this.formatJson(exportColumns, dataToExport);
        excel.export_json_to_excel({
          header: exportHeaders,
          data: exportList,
          filename: `Seguimiento clientes`,
          autoWidth: true,
          bookType: 'xlsx',
        });
        this.exportingExcel = false;
      });
    },
    formatJson(filterVal, jsonData) {
      return jsonData.map(v =>
        filterVal.map(j => {
          return v[j];
        }),
      );
    },
  },
  watch: {
    trackingKeyChanged() {
      setTimeout(() => {
        this.fetchCustomerTrackingList();
      }, 300);
    },
  },
};
</script>

<style scoped>
.tracking-table {
  .row-pending {
    background-color: #ffeb3b !important;
  }
}
</style>
